/**
 * Dynamic session configuration that reads from backend
 * Prevents hardcoded values and ensures consistency with backend settings
 */

import config from '@/config';

export interface SessionConfig {
  sessionTimeout: number;        // Total session duration in ms
  warningThreshold: number;      // Warning time before expiry in ms
  checkInterval: number;         // How often to check session status in ms
  tokenRefreshThreshold: number; // When to refresh token before expiry in ms
  expiresAt?: Date;             // When current session expires
  isActive?: boolean;           // Whether session is currently active
}

// Fallback values when backend is unavailable
const FALLBACK_CONFIG: SessionConfig = {
  sessionTimeout: config.fallbackSessionTimeout,
  warningThreshold: config.fallbackWarningThreshold,
  checkInterval: config.fallbackSessionCheckInterval,
  tokenRefreshThreshold: config.fallbackTokenRefreshThreshold,
  isActive: false
};

// Cache for session configuration
let cachedConfig: SessionConfig | null = null;
let lastConfigFetch = 0;
const CONFIG_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Fetch session configuration from backend
 */
export async function fetchSessionConfig(): Promise<SessionConfig> {
  try {
    const response = await fetch(`${config.apiUrl}${config.endpoints.admin.sessionStatus}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    });

    if (!response.ok) {
      // 401/403 means not authenticated - return fallback without error
      if (response.status === 401 || response.status === 403) {
        return FALLBACK_CONFIG;
      }
      throw new Error(`Session status request failed: ${response.status}`);
    }

    const data = await response.json();
    
    // Convert backend values to frontend format
    const sessionConfig: SessionConfig = {
      sessionTimeout: (data.expiresIn || 4 * 60 * 60) * 1000,  // Convert seconds to ms
      warningThreshold: (data.warningTime || 3 * 60) * 1000,   // Convert seconds to ms
      checkInterval: (data.checkInterval || 2 * 60) * 1000,   // Convert seconds to ms
      tokenRefreshThreshold: (data.tokenRefreshThreshold || 5 * 60) * 1000,  // Convert seconds to ms
      expiresAt: data.expiresAt ? new Date(data.expiresAt) : undefined,
      isActive: data.isActive || false
    };

    // Cache the configuration
    cachedConfig = sessionConfig;
    lastConfigFetch = Date.now();

    if (config.enableDebugLogging) {
      console.log('🔄 Session config loaded from backend:', {
        sessionTimeout: `${sessionConfig.sessionTimeout / (60 * 60 * 1000)} hours`,
        warningThreshold: `${sessionConfig.warningThreshold / (60 * 1000)} minutes`,
        expiresAt: sessionConfig.expiresAt?.toISOString(),
        isActive: sessionConfig.isActive
      });
    }

    return sessionConfig;
  } catch (error) {
    console.warn('Failed to fetch session config from backend, using fallback:', error);
    return FALLBACK_CONFIG;
  }
}

/**
 * Get session configuration (cached or fresh)
 */
export async function getSessionConfig(): Promise<SessionConfig> {
  const now = Date.now();
  
  // Return cached config if still fresh
  if (cachedConfig && (now - lastConfigFetch) < CONFIG_CACHE_DURATION) {
    return cachedConfig;
  }

  // Fetch fresh config from backend
  return await fetchSessionConfig();
}

/**
 * Force refresh session configuration
 */
export async function refreshSessionConfig(): Promise<SessionConfig> {
  cachedConfig = null;
  lastConfigFetch = 0;
  return await fetchSessionConfig();
}

/**
 * Get current cached configuration (synchronous)
 * Returns fallback if no cached config available
 */
export function getCachedSessionConfig(): SessionConfig {
  return cachedConfig || FALLBACK_CONFIG;
}

/**
 * Check if session configuration needs refresh
 */
export function shouldRefreshSessionConfig(): boolean {
  if (!cachedConfig) return true;
  const now = Date.now();
  return (now - lastConfigFetch) >= CONFIG_CACHE_DURATION;
}

/**
 * Initialize session configuration on app startup
 */
export async function initializeSessionConfig(): Promise<SessionConfig> {
  try {
    const sessionConfig = await fetchSessionConfig();
    
    if (config.enableDebugLogging) {
      console.log('✅ Session configuration initialized:', {
        source: 'backend',
        sessionTimeout: `${sessionConfig.sessionTimeout / (60 * 60 * 1000)} hours`,
        warningThreshold: `${sessionConfig.warningThreshold / (60 * 1000)} minutes`,
        isActive: sessionConfig.isActive
      });
    }
    
    return sessionConfig;
  } catch (error) {
    console.warn('Failed to initialize session config, using fallback:', error);
    return FALLBACK_CONFIG;
  }
}
